import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { useUpdateChannelPetsMutation } from 'shared/types/graphql';
import { useToast } from '@televet/kibble-ui/build/components/Toast';

const useLinkPatients = (
  channelId?: string,
): {
  addPatients: (patientIds: string[]) => Promise<void>;
  removePatients: (patientIds: string[]) => Promise<void>;
  patientsLoading: boolean;
  addOrRemovePatient: (idsToAdd: string[], idsToRemove: string[]) => Promise<void>;
} => {
  const [updateChannelPets, { loading: patientsLoading }] = useUpdateChannelPetsMutation();
  const toast = useToast();

  const addOrRemovePatient = useCallback(
    async (idsToAdd: string[] = [], idsToRemove: string[] = []) => {
      if (!channelId || !(idsToAdd.length || idsToRemove.length)) return;

      try {
        await updateChannelPets({
          refetchQueries: ['getClinicChannelViewChannel'],
          variables: {
            where: { id: channelId },
            data: {
              pets: {
                connect: idsToAdd.filter((i) => i).map((id) => ({ id })),
                disconnect: idsToRemove.filter((i) => i).map((id) => ({ id })),
              },
            },
          },
        });
      } catch (error) {
        Sentry.captureException(error);
        toast({ status: 'error', title: 'Whoops! There was a problem updating the linked patient.' });
      }
    },
    [channelId, toast, updateChannelPets],
  );

  const addPatients = useCallback(
    (patientIds: string[]) => {
      return addOrRemovePatient(patientIds, []);
    },
    [addOrRemovePatient],
  );

  const removePatients = useCallback(
    (patientIds: string[]) => {
      return addOrRemovePatient([], patientIds);
    },
    [addOrRemovePatient],
  );

  return { addPatients, removePatients, patientsLoading, addOrRemovePatient };
};

export default useLinkPatients;
